/**
 * Parse Nest/API axios errors when Voluum returns 400 on flow PUT.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { AxiosError } from 'axios';

import type { FlowApplyFailureNotice } from './apply-notice.types';

export function isWeightPathVoluumMessage(s: string): boolean {
  return /\.weight\b/.test(s) && /less than 1000/i.test(s);
}

function collectVoluumDetailMessages(details: unknown): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const visit = (v: unknown): void => {
    if (v === null || v === undefined) return;
    if (typeof v === 'string') {
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
      return;
    }
    if (Array.isArray(v)) {
      for (const x of v) visit(x);
      return;
    }
    if (typeof v === 'object') {
      const rec = v as Record<string, unknown>;
      const msg = rec.messages;
      if (Array.isArray(msg)) {
        for (const x of msg) {
          if (typeof x === 'string' && !seen.has(x)) {
            seen.add(x);
            out.push(x);
          }
        }
      }
      const desc = rec.description;
      if (typeof desc === 'string' && !seen.has(desc)) {
        seen.add(desc);
        out.push(desc);
      }
      for (const val of Object.values(rec)) visit(val);
    }
  };
  visit(details);
  return out;
}

function readBackendErrorEnvelope(
  data: unknown,
): { message: string; details: unknown } | null {
  if (!data || typeof data !== 'object') return null;
  const err = (data as { error?: unknown }).error;
  if (!err || typeof err !== 'object') return null;
  const rec = err as Record<string, unknown>;
  const message = rec.message;
  if (typeof message !== 'string') return null;
  return { message, details: rec.details };
}

export function formatAxiosVoluumApplyFailure(
  err: AxiosError,
  weightRuleLine: string,
  trackerName = 'Voluum',
): FlowApplyFailureNotice {
  const data = err.response?.data;
  const env = readBackendErrorEnvelope(data);
  const volLines = env?.details
    ? collectVoluumDetailMessages(env.details)
    : [];
  const hasWeightIssue = volLines.some(isWeightPathVoluumMessage);

  if (volLines.length > 0) {
    return {
      title: `${trackerName} rejected this update`,
      lines: [
        hasWeightIssue
          ? `The ${trackerName} API declined the payload. ${weightRuleLine} This is ${trackerName}'s rule — TrafficPulse only forwarded your JSON.`
          : `The ${trackerName} API declined the payload (TrafficPulse merely forwarded your save). Details:`,
        ...volLines.slice(0, 12),
        ...(volLines.length > 12
          ? [`…and ${volLines.length - 12} more message(s).`]
          : []),
      ],
    };
  }

  const fallback =
    env?.message ??
    (typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof (data as { message?: unknown }).message === 'string'
      ? (data as { message: string }).message
      : err.message);

  return {
    title: 'Could not apply flow',
    lines: [
      fallback ||
        'Request failed. Check your connection and try again, or inspect the network response for details.',
    ],
  };
}
